/*! ════════════════════════════════════════════════════════════════
 *  © 2026 Sick Aura Inc. All rights reserved.
 *  PROOF BALL — the index.html proof-section soccer ball, verbatim
 *  (canvas-2D marble renderer + player-behaviour physics), squeezed
 *  tiny between the so-back banner and the manifesto. No WebGL.
 *  ════════════════════════════════════════════════════════════════ */
const PRM = matchMedia('(prefers-reduced-motion: reduce)').matches;
/* ======================================================================
   SOCCER BALL — truncated icosahedron (12 pentagons + 20 hexagons). Forms
   out of white particles (swept top→bottom like gravity), then fills each
   face with the hero marble gradient, morphing over time with a moving
   light. Used for the proof-section sphere.
   ====================================================================== */
function buildSoccerBall(canvas, opts){
  opts = opts || {};
  const AURA = !!opts.aura;                       // WebGL AURA ball backend (aura-ball.js)
  const ctx = AURA ? null : canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  const R = Math.min(W,H) * (opts.radiusScale ?? 0.34);
  const spin = opts.spin ?? 0.0006;
  const PHI = (1+Math.sqrt(5))/2;
  const FREQ = 2.2, TWIST = 2.4, TILTX = -0.42;   // FREQ/TWIST match the hero marble

  const dot=(a,b)=>a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
  const cross=(a,b)=>[a[1]*b[2]-a[2]*b[1], a[2]*b[0]-a[0]*b[2], a[0]*b[1]-a[1]*b[0]];
  const norm=v=>{const m=Math.hypot(v[0],v[1],v[2])||1;return [v[0]/m,v[1]/m,v[2]/m];};
  const d2=(a,b)=>{const x=a[0]-b[0],y=a[1]-b[1],z=a[2]-b[2];return x*x+y*y+z*z;};
  const nlerp=(a,b,t)=>norm([a[0]+(b[0]-a[0])*t, a[1]+(b[1]-a[1])*t, a[2]+(b[2]-a[2])*t]);

  // icosahedron — cyclic perms of (0, ±1, ±PHI)
  const Vraw=[]; const cyc=(a,b,c)=>{Vraw.push([a,b,c],[b,c,a],[c,a,b]);};
  for(const s1 of [1,-1]) for(const s2 of [1,-1]) cyc(0, s1, s2*PHI);
  const Vn = Vraw.map(norm);
  let elen=Infinity;
  for(let i=0;i<Vn.length;i++)for(let j=i+1;j<Vn.length;j++){const d=d2(Vn[i],Vn[j]);if(d<elen)elen=d;}
  const adj = Vn.map(()=>[]); const edges=[];
  for(let i=0;i<Vn.length;i++)for(let j=i+1;j<Vn.length;j++){
    if(Math.abs(d2(Vn[i],Vn[j])-elen)<1e-4){edges.push([i,j]);adj[i].push(j);adj[j].push(i);}
  }
  const isAdj=(i,j)=>adj[i].indexOf(j)>=0;
  const tris=[];
  for(let i=0;i<12;i++)for(let j=i+1;j<12;j++){ if(!isAdj(i,j))continue;
    for(let k=j+1;k<12;k++){ if(isAdj(i,k)&&isAdj(j,k)) tris.push([i,j,k]); } }

  // 60 truncation points + "point nearest vertex v along edge v-w" map
  const P=[]; const near={};
  for(const [i,j] of edges){
    const ii=P.length; P.push(nlerp(Vn[i],Vn[j],1/3));
    const jj=P.length; P.push(nlerp(Vn[i],Vn[j],2/3));
    near[i+'_'+j]=ii; near[j+'_'+i]=jj;
  }
  const orderFace=idxs=>{
    let c=[0,0,0]; for(const i of idxs){c[0]+=P[i][0];c[1]+=P[i][1];c[2]+=P[i][2];}
    c=norm(c);
    const up = Math.abs(c[1])<0.92?[0,1,0]:[1,0,0];
    const u=norm(cross(up,c)), w=cross(c,u);
    const a=idxs.map(i=>({i,ang:Math.atan2(dot(P[i],w),dot(P[i],u))}));
    a.sort((A,B)=>A.ang-B.ang);
    const sorted=a.map(o=>o.i);
    // put edge midpoints on the sphere → panel edges hug the sphere (rounder
    // silhouette) without bulging out past it. Doming is faked by the shading.
    const bulge=1.0, rim=[];
    for(let i=0;i<sorted.length;i++){
      const A=P[sorted[i]], B=P[sorted[(i+1)%sorted.length]];
      rim.push(A);
      const m=norm([A[0]+B[0],A[1]+B[1],A[2]+B[2]]);
      rim.push([m[0]*bulge, m[1]*bulge, m[2]*bulge]);
    }
    return {pts:sorted, rim, c, az:Math.atan2(c[2],c[0])};
  };
  const faces=[];
  for(let v=0;v<12;v++) faces.push(orderFace(adj[v].map(w=>near[v+'_'+w])));            // pentagons
  for(const [i,j,k] of tris) faces.push(orderFace([near[i+'_'+j],near[j+'_'+i],near[j+'_'+k],near[k+'_'+j],near[k+'_'+i],near[i+'_'+k]]));  // hexagons

  const cx=W/2, cy=H/2;
  const ballR = R * 1.5 / 2.3;                        // on-screen sphere radius (silhouette)
  const cl=v=>v<0?0:v>255?255:v|0;
  let waveT=0, fp=0, clock=0, last=performance.now(), running=true, glr=null;

  // orientation as a 3x3 rotation matrix (row-major), started tilted a touch down
  const matMul=(a,b)=>{const r=new Array(9);for(let i=0;i<3;i++)for(let j=0;j<3;j++){let s=0;for(let k=0;k<3;k++)s+=a[i*3+k]*b[k*3+j];r[i*3+j]=s;}return r;};
  const axisAngle=(ax,ay,az,ang)=>{const l=Math.hypot(ax,ay,az)||1;ax/=l;ay/=l;az/=l;const c=Math.cos(ang),s=Math.sin(ang),t=1-c;
    return [t*ax*ax+c, t*ax*ay-s*az, t*ax*az+s*ay, t*ax*ay+s*az, t*ay*ay+c, t*ay*az-s*ax, t*ax*az-s*ay, t*ay*az+s*ax, t*az*az+c];};
  let M = axisAngle(1,0,0,-0.38);
  const applyM=p=>[M[0]*p[0]+M[1]*p[1]+M[2]*p[2], M[3]*p[0]+M[4]*p[1]+M[5]*p[2], M[6]*p[0]+M[7]*p[1]+M[8]*p[2]];
  const proj=r=>{ const f=1/(2.3 - r[2]*0.6); return [cx + r[0]*R*f*1.5, cy + r[1]*R*f*1.5]; };
  const col=(az,y,ph)=>{                              // exact hero-marble palette
    const sc=az + TWIST*y + waveT*0.28 + ph;
    const g=0.5+0.5*Math.sin(sc*FREQ);
    let cr=36+g*219, cg=78-g*50, cb=255-g*105;
    const ys=Math.sin(sc*FREQ*0.5+1.3);
    if(ys>0.86){const yl=(ys-0.86)/0.14; cr+=(255-cr)*yl; cg+=(214-cg)*yl; cb+=(10-cb)*yl;}
    return [cr,cg,cb];
  };

  // --- Motion: angular-velocity model (rad/s) with a state machine + friction so
  //     the ball coasts. A behaviour scheduler drives it like a real player — the
  //     ball never translates, only spins:
  //       dribble — running ~straight, rhythmic forward jolts of speed (touches)
  //       juggle  — chaotic multi-axis maneuvering in the air
  //       FLIGHT  — one strike -> AIRBORNE (long low-friction coast) -> IMPACT
  //                 event -> ROLLING (high friction, horizontal-biased) -> REST.
  //                 Intensity varies wildly: gentle multi-axis swirl .. blinding spin.
  //       trap    — abrupt stop, hold still
  //     Every post-formation frame writes a live `stats` object the HUD reads.
  let omega=[0,0,0], behavior=null, nextDecision=0;
  let state='IDLE', stateSince=0, lastAction='—', actionPower=0;
  let flightUntil=0, airStart=0, kickV0=0, spPrev=0, accEMA=0;
  const R_M=0.11, KMH=(0.11*3.6)*7.4;                 // scaled radius so a hard strike reads ~100+ km/h
  const stats={ speed:0, kmh:0, accel:0, maxKmh:0, rpm:0, gforce:0,
    kicks:0, touches:0, passes:0, traps:0, revolutions:0, distance:0, airtimeMs:0,
    spinType:'—', state:'IDLE', lastAction:'—', actionPower:0,
    barPeak:0, events:[], eventSeq:0, impactFlash:0, record:false };
  const pushEvent=(t)=>{ stats.events.unshift(t); if(stats.events.length>6) stats.events.pop(); stats.eventSeq++; };
  const impulse=(ax,mag)=>{ const l=Math.hypot(ax[0],ax[1],ax[2])||1; omega[0]+=ax[0]/l*mag; omega[1]+=ax[1]/l*mag; omega[2]+=ax[2]/l*mag; stats.touches++; };
  const lerp=(a,b,t)=>a+(b-a)*t;
  const classifySpin=(sp)=>{                          // spin type from dominant omega axis
    if(sp<0.4) return 'DEAD';
    const ax=Math.abs(omega[0]),ay=Math.abs(omega[1]),az=Math.abs(omega[2]),mx=Math.max(ax,ay,az);
    if(mx/(sp||1)<0.62) return 'KNUCKLE';
    if(mx===ax) return omega[0]>=0?'TOPSPIN':'BACKSPIN';
    if(mx===ay) return 'SIDESPIN';
    return 'CURL';
  };
  function launchFlight(){                             // a struck ball: pick intensity 0..1 (fat tail), map to strike
    let I=Math.pow(Math.random(),0.5); if(Math.random()<0.5) I=0.9+Math.random()*0.1;   // ~half the strikes are super-max SCREAMERS that peg the meter
    let mag=lerp(3.0,48.0,I); let axis,label;
    if(I<0.30){ axis=[Math.random()*2-1,Math.random()*2-1,Math.random()*2-1]; label=(Math.random()<0.5?'KNUCKLER':'LOFTED CHIP'); }        // gentle swirl
    else if(I<0.62){ axis=[1,Math.random()*0.5-0.25,Math.random()*0.5-0.25]; label=(Math.random()<0.5?'DRIVEN PASS':'THROUGH BALL'); }     // clean drive
    else if(I<0.88){ axis=[1,Math.random()*0.7-0.35,Math.random()*0.6-0.3]; label=(Math.random()<0.5?'VOLLEY':'PILEDRIVER'); }
    else { axis=[Math.random()*2-1,Math.random()*2-1,Math.random()*2-1]; label=(Math.random()<0.5?'BICYCLE':'SCREAMER'); }                  // blinding
    if(PRM){ mag=Math.min(mag,7); }                   // reduced-motion: keep it a calm swirl
    omega=[0,0,0]; impulse(axis,mag);
    state='AIRBORNE'; stateSince=clock; airStart=clock; flightUntil=clock+lerp(900,4200,I);
    kickV0=Math.hypot(omega[0],omega[1],omega[2]);
    stats.kicks++; stats.passes++; lastAction=label; actionPower=I; pushEvent(label);
  }
  function decide(){
    const r=Math.random();
    if(r<0.28){                                        // DRIBBLE — straight-line run, forward jolts
      const axis=[1, Math.random()*0.6-0.3, Math.random()*0.5-0.25];
      behavior={type:'dribble', axis, sign:(Math.random()<0.5?1:-1), steps:4+Math.floor(Math.random()*6), i:0, next:clock, cadence:200+Math.random()*180, jolt:4.5+Math.random()*4};
      state='DRIBBLE'; stateSince=clock; lastAction='DRIBBLE'; actionPower=0.3;
    } else if(r<0.50){                                 // JUGGLE — chaotic maneuvering
      behavior={type:'juggle', until:clock+1300+Math.random()*2400, next:clock};
      state='JUGGLE'; stateSince=clock; lastAction='JUGGLE'; actionPower=0.4; pushEvent('JUGGLE');
    } else if(r<0.86){                                 // FLIGHT — the star: strike -> airborne -> impact -> roll -> rest
      launchFlight();
    } else {                                           // trap — abrupt stop
      omega=[0,0,0]; nextDecision=clock+300+Math.random()*800;
      state='TRAP'; stateSince=clock; lastAction='TRAP'; actionPower=0; stats.traps++; pushEvent('TRAP');
    }
  }

  function frame(now){
    if(!running) return;
    const dt=Math.min(64, now-last); last=now;
    waveT += dt*0.001; clock += dt;
    if(fp<1) fp=Math.min(1, fp + dt/5600);            // ~5.6s formation — wireframe -> fill -> glow, slow enough to watch

    if(fp<1){
      M = matMul(axisAngle(0.25,1,0.12, 0.0006*dt), M);   // calm spin while it forms
    } else {
      if(behavior){
        if(behavior.type==='dribble'){
          if(clock>=behavior.next){                        // each step = a forward jolt of speed
            impulse([behavior.axis[0]*behavior.sign, behavior.axis[1]*behavior.sign, behavior.axis[2]*behavior.sign], behavior.jolt);
            behavior.i++; behavior.next += behavior.cadence*(0.8+Math.random()*0.5);
            stats.kicks++; lastAction='TAP';
            if(behavior.i>=behavior.steps){ behavior=null; state='ROLLING'; stateSince=clock; nextDecision=clock+250+Math.random()*900; }
          }
        } else if(behavior.type==='juggle'){
          if(clock>=behavior.next){ impulse([Math.random()*2-1,Math.random()*2-1,Math.random()*2-1], 4+Math.random()*6); behavior.next += 170+Math.random()*430; }
          if(clock>=behavior.until){ behavior=null; nextDecision=clock+200+Math.random()*700; state='ROLLING'; stateSince=clock; }
        }
      } else if(state==='AIRBORNE'){                    // FLIGHT: coast, then IMPACT -> ROLL (or SOFT TRAP)
        const spa=Math.hypot(omega[0],omega[1],omega[2]);
        if(clock>=flightUntil || spa<kickV0*0.34){
          stats.airtimeMs=clock-airStart;
          if(spa<0.9){ state='REST'; omega=[0,0,0]; lastAction='SOFT TRAP'; pushEvent('TRAP'); nextDecision=clock+500+Math.random()*1400; }
          else { impulse([-omega[1],omega[0],omega[2]*0.3], spa*0.28);   // deflection off "the ground"
            state='ROLLING'; stateSince=clock; const lab=(spa>10?'HEADER':(spa>5?'BOUNCE':'HALF-VOLLEY'));
            lastAction=lab; pushEvent(lab); stats.impactFlash=1; }
        }
      } else if(state==='ROLLING'){
        if(Math.hypot(omega[0],omega[1],omega[2])<0.9){ state='REST'; omega=[0,0,0]; lastAction='SETTLED'; pushEvent('REST'); nextDecision=clock+500+Math.random()*1600; }
      } else if(clock>=nextDecision){ decide(); }
      let hl; if(state==='AIRBORNE') hl=2600; else if(state==='ROLLING') hl=430; else hl=650;   // state-driven half-life
      const k=Math.pow(0.5, dt/hl); omega[0]*=k; omega[1]*=k; omega[2]*=k;   // friction (coast)
      if(state==='ROLLING'){ const yk=Math.pow(0.5, dt/240); omega[1]*=yk; }  // settle toward a horizontal roll
      let sp=Math.hypot(omega[0],omega[1],omega[2]); const MAX = state==='AIRBORNE'?50:17;
      if(sp>MAX){ const f=MAX/sp; omega[0]*=f; omega[1]*=f; omega[2]*=f; sp=MAX; }
      if(sp>0.0006){ const ang=sp*dt/1000; M = matMul(axisAngle(omega[0],omega[1],omega[2], ang), M); }
      // ---- telemetry: derive live stats from omega (single honest source) ----
      const dts=Math.max(0.001, dt/1000), kmh=Math.min(140, sp*KMH);
      const acc=(sp-spPrev)/dts; accEMA=accEMA+(acc-accEMA)*0.25; spPrev=sp;
      if(kmh>stats.maxKmh){ stats.maxKmh=kmh; stats.record=(kmh>60); } else stats.record=false;
      stats.revolutions += sp*dts/(2*Math.PI);
      if(state==='ROLLING'||state==='DRIBBLE') stats.distance += sp*R_M*dts;
      stats.speed=sp; stats.kmh=kmh; stats.accel=accEMA*R_M; stats.rpm=sp/(2*Math.PI)*60; stats.gforce=sp*sp*R_M/9.81;
      stats.spinType=classifySpin(sp); stats.state=state; stats.lastAction=lastAction; stats.actionPower=actionPower;
      stats.barPeak=Math.max(stats.barPeak*0.985, Math.min(1, kmh/140));
      if(stats.impactFlash>0) stats.impactFlash=Math.max(0, stats.impactFlash-dts*4);
    }

    if(AURA){
      if(!glr && window.createAuraBall) glr=window.createAuraBall(canvas);
      if(glr) glr.frame(M, fp);                  // EXACT aura.html ball, physics-driven
      requestAnimationFrame(frame);
      return;
    }
    ctx.clearRect(0,0,W,H);
    const PR = P.map(p=>{ const r=applyM(p); return {r, s:proj(r)}; });
    const grow = fp;

    // white particles form first
    if(fp<1){
      ctx.fillStyle='#fff';
      for(const q of PR){ if(q.r[2]<0) continue; const d=(q.r[2]+1)/2;
        ctx.globalAlpha=(1-fp)*(0.25+0.6*d); const s=1.4+2.2*d;
        ctx.fillRect(q.s[0]-s/2, q.s[1]-s/2, s, s); }
    }

    // round sphere body behind the panels — rounds the silhouette, fills seams
    if(grow>0.02){
      const bg=ctx.createRadialGradient(cx-ballR*0.32, cy-ballR*0.36, ballR*0.08, cx, cy, ballR*1.04);
      bg.addColorStop(0,'#191534'); bg.addColorStop(0.72,'#0c0a1e'); bg.addColorStop(1,'#050410');
      ctx.globalAlpha=grow; ctx.fillStyle=bg;
      ctx.beginPath(); ctx.arc(cx,cy,ballR*1.015,0,6.2831853); ctx.fill();
    }

    // panels — softly puffed (simplest way): one per-panel radial gradient, brighter at
    // the centre and shaded toward the rim, so each facet reads as gently inflated. The
    // radial is symmetric, so corners go uniformly dark and the seam intersections stop
    // looking pointy. No geometry change → never spiky. Colours unchanged. Tune PUFF below.
    for(const f of faces){
      const cr=applyM(f.c);
      if(cr[2] < 0.02) continue;                       // back-face cull
      let rev=fp*1.7 - (1-(f.c[1]+1)/2)*0.9; rev=rev<0?0:rev>1?1:rev;
      if(rev<=0) continue;
      const base=col(f.az, f.c[1], 0);
      const light=0.55 + 0.45*Math.max(0, cr[0]*-0.32 + cr[1]*0.44 + cr[2]*0.84);   // sphere shading (bright floor)
      const rp=f.rim.map(p=>proj(applyM(p)));
      let scx=0, scy=0; for(const s of rp){ scx+=s[0]; scy+=s[1]; } scx/=rp.length; scy/=rp.length;
      let pr=0; for(const s of rp){ const dd=Math.hypot(s[0]-scx,s[1]-scy); if(dd>pr)pr=dd; }
      ctx.beginPath();
      for(let i=0;i<rp.length;i++){ const s=rp[i]; if(i===0) ctx.moveTo(s[0],s[1]); else ctx.lineTo(s[0],s[1]); }
      ctx.closePath();
      // Subtle dome: gentle centre lift + mild rim shade only. Bright vivid panels,
      // no heavy edge shadow; thin light seam. Colours stay at true intensity.
      const hi=light*1.08, ed=light*0.74;              // PUFF: keep gentle
      const rg=ctx.createRadialGradient(scx,scy,pr*0.06, scx,scy,pr*1.05);
      rg.addColorStop(0,   'rgb('+cl(base[0]*hi)+','+cl(base[1]*hi)+','+cl(base[2]*hi)+')');
      rg.addColorStop(0.62,'rgb('+cl(base[0]*light)+','+cl(base[1]*light)+','+cl(base[2]*light)+')');
      rg.addColorStop(1,   'rgb('+cl(base[0]*ed)+','+cl(base[1]*ed)+','+cl(base[2]*ed)+')');
      ctx.globalAlpha = rev * (cr[2]<0.13 ? cr[2]/0.13 : 1);
      ctx.fillStyle=rg; ctx.fill();
      ctx.lineWidth=1.0; ctx.strokeStyle='rgba(10,10,22,0.26)'; ctx.stroke();
    }

    // rim shadow (volume) + fixed glossy highlight (so the sheen slides as it spins)
    if(grow>0.02){
      ctx.globalAlpha=grow;
      const vg=ctx.createRadialGradient(cx,cy,ballR*0.58, cx,cy,ballR*1.03);
      vg.addColorStop(0,'rgba(0,0,0,0)'); vg.addColorStop(1,'rgba(0,0,0,0.55)');
      ctx.fillStyle=vg; ctx.beginPath(); ctx.arc(cx,cy,ballR*1.03,0,6.2831853); ctx.fill();
      ctx.globalCompositeOperation='lighter';
      const sg=ctx.createRadialGradient(cx-ballR*0.34, cy-ballR*0.4, 0, cx-ballR*0.34, cy-ballR*0.4, ballR*0.95);
      sg.addColorStop(0,'rgba(255,255,255,'+(0.3*grow)+')'); sg.addColorStop(0.4,'rgba(205,222,255,'+(0.09*grow)+')'); sg.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=sg; ctx.beginPath(); ctx.arc(cx,cy,ballR*1.05,0,6.2831853); ctx.fill();
      ctx.globalCompositeOperation='source-over';
    }

    ctx.globalAlpha=1;
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  return { setProgress(){}, getStats(){ return stats; }, pause(){ running=false; },
           resume(){ if(running) return; running=true; fp=0; omega=[0,0,0]; behavior=null; nextDecision=0;
             state='IDLE'; stateSince=0; lastAction='—'; actionPower=0; flightUntil=0; airStart=0; kickV0=0; spPrev=0; accEMA=0;
             stats.speed=stats.kmh=stats.accel=stats.maxKmh=stats.rpm=stats.gforce=0;
             stats.kicks=stats.touches=stats.passes=stats.traps=0; stats.revolutions=stats.distance=stats.airtimeMs=0;
             stats.spinType='—'; stats.state='IDLE'; stats.lastAction='—'; stats.actionPower=0;
             stats.barPeak=0; stats.events=[]; stats.eventSeq=0; stats.impactFlash=0; stats.record=false;
             last=performance.now(); requestAnimationFrame(frame); } };
}

/* ── init: tiny, and it only runs while on screen ── */
(function(){
  var cv=document.getElementById('sa-proof-ball');
  if(!cv) return;
  var api=buildSoccerBall(cv,{spin:0.0006, aura:true});
  if(!('IntersectionObserver' in window)) return;   // no IO: just let it run
  api.pause();                                      // hold — the show starts when you arrive
  new IntersectionObserver(function(entries){
    entries.forEach(function(e){
      if(e.isIntersecting) api.resume();            // formation plays on EVERY arrival
      else api.pause();
    });
  },{threshold:0.3}).observe(cv);
})();
